"use client";

import React from "react";
import { toast as sonnerToast } from "sonner";
import { XIcon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const toastVariants = cva(
  "flex items-center justify-between gap-4 rounded-(--border-radius-150) p-5 shadow-lg w-full md:max-w-[364px] pointer-events-auto",
  {
    variants: {
      variant: {
        default: "bg-bg-fill-inverse text-inverse",
        error: "bg-bg-fill-critical-active text-inverse",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

interface ToastProps {
  id: string | number;
  text: string;
  secondaryText?: string;
  variant?: VariantProps<typeof toastVariants>["variant"];
}

const toastFn = (
  text: string,
  secondaryText?: string,
  toast?: Omit<ToastProps, "id" | "variant" | "text" | "secondaryText">
) => {
  return sonnerToast.custom(id => (
    <Toast text={text} secondaryText={secondaryText} id={id} {...toast} variant="default" />
  ));
};

toastFn.default = (
  text: string,
  secondaryText?: string,
  toast?: Omit<ToastProps, "id" | "variant" | "text" | "secondaryText">
) => {
  return sonnerToast.custom(id => (
    <Toast text={text} secondaryText={secondaryText} id={id} {...toast} variant="default" />
  ));
};

toastFn.error = (
  text: string,
  secondaryText?: string,
  toast?: Omit<ToastProps, "id" | "variant" | "text" | "secondaryText">
) => {
  return sonnerToast.custom(id => (
    <Toast text={text} secondaryText={secondaryText} id={id} {...toast} variant="error" />
  ));
};

export const toast = toastFn;

// This follows the headless approach reccomended by Sonner https://sonner.emilkowal.ski/styling#headless-/-tailwind-css
function Toast(props: ToastProps) {
  const { text, secondaryText, id, variant } = props;

  return (
    <div className={cn(toastVariants({ variant }))}>
      <div className="flex flex-1 items-center">
        <div className="w-full">
          <p className="body-md font-medium">{text}</p>
          {secondaryText && <p className="mt-1 body-md">{secondaryText}</p>}
        </div>
      </div>

      <Button
        size="icon"
        variant="ghost"
        className="p-0 bg-transparent border-none text-inverse"
        onClick={() => sonnerToast.dismiss(id)}
      >
        <XIcon className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </Button>
    </div>
  );
}
